// Implements: F-IDN-012, F-UIX-009, F-UIX-007
/**
 * P2 `/recuperar` (v3 `vForgot`): one address and one action. Pure presentation: the value, the
 * refusals and the handlers arrive as props. The validation sentence sits under the field
 * (`Field`'s error, tied by `aria-describedby`); the server's failure sits above it (`role="alert"`),
 * as the v3 note says; a `429` wait arrives as the `notice` slot.
 */

import type { ReactElement, ReactNode, RefObject } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, errorId } from "../../../shared/ui/molecules/Field";
import login from "./LoginForm.module.css";
import { RecoveryBrand } from "./RecoveryBrand";

export const FORGOT_EMAIL_FIELD_ID = "recovery-email";

export interface ForgotPasswordFormProps {
  readonly email: string;
  readonly onEmailChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
  /** The bucket is closed: the button waits with the notice. */
  readonly disabled: boolean;
  readonly fieldError: string | undefined;
  readonly formError: string | undefined;
  readonly notice?: ReactNode;
  readonly loginPath: string;
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
  readonly emailRef: RefObject<HTMLInputElement | null>;
}

export function ForgotPasswordForm({
  email,
  onEmailChange,
  onSubmit,
  submitting,
  disabled,
  fieldError,
  formError,
  notice,
  loginPath,
  headingRef,
  emailRef,
}: ForgotPasswordFormProps): ReactElement {
  const { t } = useTranslation();
  return (
    <form
      className={login.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <RecoveryBrand />
      <div>
        <h1 className={login.title} tabIndex={-1} ref={headingRef}>
          {t("auth.recovery.title")}
        </h1>
        <p className={login.subtitle}>{t("auth.recovery.subtitle")}</p>
      </div>
      {notice}
      {formError === undefined ? null : (
        <p className={login.error} role="alert">
          <Icon name="alert-circle" size="sm" />
          <span>{formError}</span>
        </p>
      )}
      <Field id={FORGOT_EMAIL_FIELD_ID} label={t("auth.login.emailLabel")} error={fieldError}>
        <Input
          id={FORGOT_EMAIL_FIELD_ID}
          type="email"
          value={email}
          onChange={onEmailChange}
          autoComplete="username"
          placeholder={t("auth.recovery.emailPlaceholder")}
          invalid={fieldError !== undefined}
          describedBy={fieldError === undefined ? undefined : errorId(FORGOT_EMAIL_FIELD_ID)}
          inputRef={emailRef}
        />
      </Field>
      <Button variant="primary" type="submit" loading={submitting} disabled={disabled}>
        {t("auth.recovery.submit")}
      </Button>
      <Link to={loginPath} className={buttonClassName("ghost")}>
        {t("auth.recovery.back")}
      </Link>
    </form>
  );
}
